import { useEffect, useMemo, useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab,
  TextField, List, ListItemButton, ListItemAvatar, ListItemText, Avatar,
  Box, Typography, CircularProgress,
} from '@mui/material'
import { Map as MapIcon, People } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { socialApi } from '@api/endpoints/social'
import { adventuresApi } from '@api/endpoints/adventures'
import { ChatReferenceKind, type ChatReferenceInput } from '@api/types/chat'

/**
 * Picking an adventure or group to point at from a message.
 *
 * Only things the *sender* can reach are offered. That is a convenience, not the security
 * boundary — the server re-decides for every reader when the message is read, so offering a
 * shortlist here cannot widen anybody else's view.
 */

interface ChatReferencePickerDialogProps {
  open: boolean
  onClose: () => void
  onPick: (reference: ChatReferenceInput, label: string) => void
}

export function ChatReferencePickerDialog({ open, onClose, onPick }: ChatReferencePickerDialogProps) {
  const [tab, setTab] = useState<'adventures' | 'groups'>('adventures')
  const [search, setSearch] = useState('')

  /**
   * The term the server is asked for, a beat behind the box. 300 ms, as elsewhere in this app.
   *
   * The local filter below stays: it narrows what is on screen the moment somebody types, so the
   * list feels immediate while the request for the wider set is in flight.
   */
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const groupsQ = useQuery({
    queryKey: ['chat-ref', 'groups'],
    queryFn: () => socialApi.getMyGroups(),
    enabled: open && tab === 'groups',
    staleTime: 5 * 60_000,
  })

  const adventuresQ = useQuery({
    queryKey: ['chat-ref', 'adventures', debounced],
    // The lookup, not the full list: this picker reads a title, a one-line subtitle and a thumbnail,
    // and the full list sends ninety-five fields a row -- 7 MB on an account holding the bulk
    // imports, a megabyte of it fullDescription that nothing here renders.
    queryFn: () => adventuresApi.getMyAdventureLookup({ q: debounced || undefined, take: 50 }),
    enabled: open && tab === 'adventures',
    staleTime: 5 * 60_000,
  })

  const loading = tab === 'groups' ? groupsQ.isLoading : adventuresQ.isLoading

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (tab === 'groups') {
      return (groupsQ.data ?? [])
        .filter((g) => !q || g.name.toLowerCase().includes(q))
        .map((g) => ({
          id: g.id,
          title: g.name,
          subtitle: g.description ?? '',
          image: g.groupImage,
          kind: ChatReferenceKind.Group as number,
        }))
    }
    return (adventuresQ.data ?? [])
      .filter((a) => !q || (a.title ?? '').toLowerCase().includes(q))
      .map((a) => ({
        id: a.id,
        title: a.title,
        subtitle: a.quickDescription ?? '',
        image: a.thumbnailUrl,
        kind: ChatReferenceKind.Adventure as number,
      }))
  }, [tab, search, groupsQ.data, adventuresQ.data])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Add to your message</DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="fullWidth" sx={{ mb: 1.5 }}>
          <Tab value="adventures" icon={<MapIcon fontSize="small" />} iconPosition="start" label="Adventures" sx={{ minHeight: 44, textTransform: 'none' }} />
          <Tab value="groups" icon={<People fontSize="small" />} iconPosition="start" label="Groups" sx={{ minHeight: 44, textTransform: 'none' }} />
        </Tabs>

        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <Typography sx={{ py: 3, fontSize: 13, color: 'text.secondary' }}>
            {search.trim() ? 'Nothing matches that.' : 'Nothing here to add yet.'}
          </Typography>
        ) : (
          <List dense sx={{ maxHeight: 320, overflowY: 'auto', mt: 1 }}>
            {rows.map((row) => (
              <ListItemButton
                key={`${row.kind}:${row.id}`}
                onClick={() => { onPick({ kind: row.kind, id: row.id }, row.title); onClose() }}
              >
                <ListItemAvatar>
                  <Avatar src={row.image || undefined} variant="rounded" sx={{ width: 36, height: 36 }}>
                    {row.kind === ChatReferenceKind.Group ? <People fontSize="small" /> : <MapIcon fontSize="small" />}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={row.title}
                  secondary={row.subtitle || undefined}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 700, noWrap: true }}
                  secondaryTypographyProps={{ fontSize: 12, noWrap: true }}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  )
}
